import React, { useCallback, useEffect, useMemo, useState } from "react";
import { consumeNavPath } from "@/lib/adminNav";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { canEditTab } from "@/lib/permissions";
import { formatDate } from "@/lib/dates";
import {
  format,
  addDays,
  addMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isSameDay,
  isSameMonth,
} from "date-fns";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as UiCalendar } from "@/components/ui/calendar";
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Copy,
  Edit,
  Eye,
  Plus,
  RefreshCw,
  Repeat,
  Trash2,
  Truck,
  Car,
  Wrench,
  X,
  LayoutGrid,
  Calendar,
  CalendarRange,
  ArrowLeftRight,
  CalendarOff,
  CheckSquare,
  Square,
  Send,
  ChevronsUpDown,
  Rows3,
} from "lucide-react";

import { toast } from "@/hooks/use-toast";

import type {
  ShiftRow,
  DriverRow,
  VehicleRow,
  ShiftTypeRow,
  HolidayRow,
  MissingVehicleRow,
  FreeVehicleRow,
  BookingConflictRow,
  AllocationGroupRow,
  CreateShiftForm,
  CalendarViewMode,
  DragData,
  RosterGroupRow,
} from "@/components/roster/types";
import {
  NZ_WEEK_STARTS_ON,
  defaultCreateShiftForm,
  combineDateAndTime,
  calcWorkedHours,
  getStatusBadgeVariant,
  getShiftColour,
  driverDisplayName,
  generateRepeatDates,
  formatTime12,
  DAY_LABELS,
} from "@/components/roster/utils";
import {
  loadWorkTimeRules,
  validateShift,
  validateShiftBatch,
  checkWorkTimeRules,
  DEFAULT_WORK_TIME_RULES,
  type ShiftSpan,
  type WorkTimeRules,
  type WorkTimeViolation,
} from "@/components/roster/workTimeRulesUtils";
import { loadVehicleTypes, getIconStyle, getVehicleTypeColour, type VehicleTypeRow } from "@/lib/vehicleTypes";
import type { AllocationMessageOptions } from "@/components/roster/allocationMessage";
import AllocationMessageDialog from "@/components/roster/AllocationMessageDialog";

import ShiftCard from "@/components/roster/ShiftCard";
import ShiftFormDialog from "@/components/roster/ShiftFormDialog";
import TimelineDayView from "@/components/roster/TimelineDayView";
import RosterFilterBar from "@/components/roster/RosterFilterBar";
import type {
  RosterFilters,
  RosterSortOption,
} from "@/components/roster/RosterFilterBar";
import WeekTimelineView from "@/components/roster/WeekTimelineView";
import AdminTimeOffPanel from "@/components/roster/AdminTimeOffPanel";
import SwapRequestsPanel from "@/components/roster/SwapRequestsPanel";
import FindCoverDialog from "@/components/roster/FindCoverDialog";
import PublishValidationDialog, {
  type PublishConflict,
} from "@/components/roster/PublishValidationDialog";
import RosterDemandTab from "@/pages/RosterDemandTab";
import DayTrendOverlay from "@/pages/DayTrendOverlay";


const getShiftGroupValue = (shift: ShiftRow): string | null => {
  const candidate =
    (shift as any)?.schedule_group ??
    (shift as any)?.allocation_group ??
    (shift as any)?.group_name ??
    (shift as any)?.roster_group ??
    null;

  if (!candidate || typeof candidate !== "string") return null;

  const trimmed = candidate.trim();
  return trimmed.length > 0 ? trimmed : null;
};

// Vehicle types are canonical now (single source: vehicle_types table), so
// no more fuzzy substring bucketing needed -- the value itself is the group.
const getVehicleGroupLabel = (shift: ShiftRow): string => {
  const raw = (shift.vehicle_type || "").toString().trim();
  return raw || "Unassigned";
};

const compareText = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

// Same overnight-wrap convention as calcWorkedHours: an end time <= start
// time is treated as crossing midnight rather than a zero/negative shift.
const toMinutesRange = (startHHMM: string, endHHMM: string): [number, number] => {
  const [sh, sm] = startHHMM.split(":").map(Number);
  const [eh, em] = endHHMM.split(":").map(Number);
  const start = sh * 60 + sm;
  let end = eh * 60 + em;
  if (end <= start) end += 24 * 60;
  return [start, end];
};

const timeRangesOverlap = (aStart: string, aEnd: string, bStart: string, bEnd: string): boolean => {
  const [as, ae] = toMinutesRange(aStart, aEnd);
  const [bs, be] = toMinutesRange(bStart, bEnd);
  return as < be && bs < ae;
};

interface VehicleConflict {
  shift_date: string;
  driver_name: string;
  start_hhmm: string;
  end_hhmm: string;
}

// A physical vehicle can't be driven by two people at once -- checks
// whether vehicleNumber is already rostered (on a non-cancelled shift) to
// someone else at an overlapping time on any of the given dates.
// excludeShiftIds lets an in-progress edit ignore the shift(s) it's
// editing so saving without changes doesn't flag itself as a conflict.
const findVehicleConflicts = async (
  vehicleNumber: string,
  dates: string[],
  startTime: string,
  endTime: string,
  excludeShiftIds: string[] = []
): Promise<VehicleConflict[]> => {
  const { data, error } = await supabase
    .from("roster_shifts")
    .select("id, shift_date, driver_name, start_hhmm, end_hhmm")
    .eq("vehicle_number", vehicleNumber)
    .in("shift_date", dates)
    .neq("status", "cancelled");

  if (error) throw error;

  return (data || [])
    .filter((row) => !excludeShiftIds.includes(row.id))
    .filter((row) => timeRangesOverlap(startTime, endTime, row.start_hhmm, row.end_hhmm))
    .map((row) => ({
      shift_date: row.shift_date,
      driver_name: row.driver_name,
      start_hhmm: row.start_hhmm,
      end_hhmm: row.end_hhmm,
    }));
};

const RosterPage: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) sees only their linked driver(s)' shifts here, read-only
  // unless explicitly given edit rights. Unrestricted (the default --
  // everyone today) is unaffected.
  const { effectivePermissions } = useAppContext();
  const canEdit = canEditTab(effectivePermissions, "roster");
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<CalendarViewMode>("week");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [showCoverage, setShowCoverage] = useState(true);

  const [shifts, setShifts] = useState<ShiftRow[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);
  const [workTimeRules, setWorkTimeRules] = useState<WorkTimeRules>(DEFAULT_WORK_TIME_RULES);

  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);
    loadWorkTimeRules().then(setWorkTimeRules);
  }, []);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [shiftTypes, setShiftTypes] = useState<ShiftTypeRow[]>([]);
  const [holidays, setHolidays] = useState<HolidayRow[]>([]);
  const [missingVehicles, setMissingVehicles] = useState<MissingVehicleRow[]>([]);
  const [freeVehicles, setFreeVehicles] = useState<FreeVehicleRow[]>([]);
  const [bookingConflicts, setBookingConflicts] = useState<BookingConflictRow[]>([]);
  const [allocationGroups, setAllocationGroups] = useState<AllocationGroupRow[]>([]);
  const [rosterGroups, setRosterGroups] = useState<RosterGroupRow[]>([]);

  const [allocMsgOptions, setAllocMsgOptions] = useState<AllocationMessageOptions>({
    includeSedanSuv: true,
    includeTmv: true,
    includeSts: true,
    includeBreaks: true,
  });
  const [allocMsgDateStr, setAllocMsgDateStr] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState<ShiftRow | null>(null);
  const [formData, setFormData] = useState<CreateShiftForm>(
    defaultCreateShiftForm(new Date())
  );
  const [showCancelled, setShowCancelled] = useState(false);
  const [activeTab, setActiveTab] = useState("calendar");

  // Deep-link from the Site Map (Settings -> Site Map) -- see adminNav.ts.
  useEffect(() => {
    const path = consumeNavPath();
    if (path?.[0]) setActiveTab(path[0]);
  }, []);

  // Demand/Allocation are hidden from a restricted user's tab list above --
  // redirect away if a deep-link or stale state ever lands on one directly.
  useEffect(() => {
    if (scopedDriverNames && (activeTab === "demand" || activeTab === "allocation")) {
      setActiveTab("calendar");
    }
  }, [scopedDriverNames, activeTab]);

  const [repeatConfirmOpen, setRepeatConfirmOpen] = useState(false);
  const [repeatDates, setRepeatDates] = useState<string[]>([]);

  const [dragOverDate, setDragOverDate] = useState<string | null>(null);

  const [rosterFilters, setRosterFilters] = useState<RosterFilters>({
    shiftTypes: [],
    vehicleGroups: [],
    drivers: [],
    sortBy: "driver_name_az",
    groupedVehicleView: [],
  });

  const [recurringChoiceOpen, setRecurringChoiceOpen] = useState(false);
  const [recurringChoiceShift, setRecurringChoiceShift] = useState<ShiftRow | null>(
    null
  );
  const [recurringChoiceAction, setRecurringChoiceAction] = useState<
    "edit" | "delete" | "cancel"
  >("edit");
  const [editSeriesMode, setEditSeriesMode] = useState<"single" | "following" | "series">(
    "single"
  );
  const [seriesShiftCount, setSeriesShiftCount] = useState(0);
  // How many of the series' shifts fall on or after the one being edited/deleted.
  const [followingShiftCount, setFollowingShiftCount] = useState(0);

  const [findCoverShift, setFindCoverShift] = useState<ShiftRow | null>(null);
  const [findCoverOpen, setFindCoverOpen] = useState(false);

  const [trendOverlayOpen, setTrendOverlayOpen] = useState(false);
  const [trendOverlayDate, setTrendOverlayDate] = useState<string | null>(null);

  type ApprovedTimeOffRow = {
    id: string;
    driver_name: string;
    start_date: string;
    end_date: string;
    start_time: string | null;
    end_time: string | null;
    request_type: "partial_day" | "full_day" | "multi_day";
    status: string;
  };

  const [approvedTimeOff, setApprovedTimeOff] = useState<ApprovedTimeOffRow[]>([]);
  const [publishValidationOpen, setPublishValidationOpen] = useState(false);
  const [publishConflicts, setPublishConflicts] = useState<PublishConflict[]>([]);
  const [publishScope, setPublishScope] = useState<{
    kind: "day" | "all" | "selected";
    dateStr?: string;
  }>({ kind: "all" });
  const [publishing, setPublishing] = useState(false);

  const [selectedShiftIds, setSelectedShiftIds] = useState<string[]>([]);

  const { rangeStart, rangeEnd, rangeDays } = useMemo(() => {
    if (viewMode === "day") {
      return {
        rangeStart: currentDate,
        rangeEnd: currentDate,
        rangeDays: [currentDate],
      };
    }

    if (viewMode === "week") {
      const ws = startOfWeek(currentDate, { weekStartsOn: NZ_WEEK_STARTS_ON });
      const we = endOfWeek(currentDate, { weekStartsOn: NZ_WEEK_STARTS_ON });
      const days: Date[] = [];
      for (let i = 0; i < 7; i++) days.push(addDays(ws, i));
      return { rangeStart: ws, rangeEnd: we, rangeDays: days };
    }

    const ms = startOfMonth(currentDate);
    const me = endOfMonth(currentDate);
    const calStart = startOfWeek(ms, { weekStartsOn: NZ_WEEK_STARTS_ON });
    const calEnd = endOfWeek(me, { weekStartsOn: NZ_WEEK_STARTS_ON });
    const days: Date[] = [];
    let d = calStart;
    while (d <= calEnd) {
      days.push(d);
      d = addDays(d, 1);
    }
    return { rangeStart: calStart, rangeEnd: calEnd, rangeDays: days };
  }, [currentDate, viewMode]);

  const rangeStartStr = format(rangeStart, "yyyy-MM-dd");
  const rangeEndStr = format(rangeEnd, "yyyy-MM-dd");

  const driverGroupsMap = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const d of drivers) {
      const groups = new Set<string>();
      if (Array.isArray(d.roster_groups)) {
        for (const g of d.roster_groups) {
          if (typeof g === "string" && g.trim()) groups.add(g.trim());
        }
      }
      map[d.driver_name] = groups;
    }
    return map;
  }, [drivers]);

  const sortShiftRows = useCallback(
    (rows: ShiftRow[], sortBy: RosterSortOption) => {
      const cloned = [...rows];

      cloned.sort((a, b) => {
        switch (sortBy) {
          case "driver_name_az":
            return compareText(driverDisplayName(a), driverDisplayName(b));
          case "driver_name_za":
            return compareText(driverDisplayName(b), driverDisplayName(a));

          case "start_time_asc":
            return a.start_hhmm.localeCompare(b.start_hhmm);
          case "start_time_desc":
            return b.start_hhmm.localeCompare(a.start_hhmm);

          case "vehicle_type_grouped": {
            const groupCompare = compareText(
              getVehicleGroupLabel(a),
              getVehicleGroupLabel(b)
            );
            if (groupCompare !== 0) return groupCompare;

            const driverCompare = compareText(
              driverDisplayName(a),
              driverDisplayName(b)
            );
            if (driverCompare !== 0) return driverCompare;

            return a.start_hhmm.localeCompare(b.start_hhmm);
          }

          default:
            return a.start_hhmm.localeCompare(b.start_hhmm);
        }
      });

      return cloned;
    },
    []
  );

  const applyFilters = (shiftList: ShiftRow[]): ShiftRow[] => {
    let result = shiftList;

    // Restricted user -- only their linked driver(s)' shifts, regardless
    // of whatever they've picked in the driver filter above.
    if (scopedDriverNames) {
      const scoped = new Set(scopedDriverNames);
      result = result.filter((s) => scoped.has(s.driver_name));
    }

    if (rosterFilters.shiftTypes.length > 0) {
      result = result.filter((s) =>
        rosterFilters.shiftTypes.includes(s.shift_type_code)
      );
    }

    if (rosterFilters.vehicleGroups.length > 0) {
      const selected = new Set(rosterFilters.vehicleGroups);
      result = result.filter((s) => {
        const shiftGroup = getShiftGroupValue(s);
        if (shiftGroup && selected.has(shiftGroup)) return true;

        const driverGroups = driverGroupsMap[s.driver_name];
        if (driverGroups) {
          for (const g of driverGroups) {
            if (selected.has(g)) return true;
          }
        }
        return false;
      });
    }

    if (
      Array.isArray(rosterFilters.groupedVehicleView) &&
      rosterFilters.groupedVehicleView.length > 0
    ) {
      const selectedVehicleGroups = new Set(rosterFilters.groupedVehicleView);
      result = result.filter((s) =>
        selectedVehicleGroups.has(getVehicleGroupLabel(s))
      );
    }

    if (rosterFilters.drivers.length > 0) {
      result = result.filter((s) =>
        rosterFilters.drivers.includes(s.driver_name)
      );
    }

    return sortShiftRows(result, rosterFilters.sortBy || "driver_name_az");
  };

  const filteredShifts = useMemo(
    () => applyFilters(shifts),
    [shifts, rosterFilters, sortShiftRows, scopedDriverNames]
  );

  const filteredShiftsByDate = useMemo(() => {
    const map: Record<string, ShiftRow[]> = {};
    for (const day of rangeDays) {
      map[format(day, "yyyy-MM-dd")] = [];
    }
    for (const shift of filteredShifts) {
      if (!showCancelled && shift.status === "cancelled") continue;
      const key = shift.shift_date;
      if (map[key]) map[key].push(shift);
    }
    return map;
  }, [filteredShifts, rangeDays, showCancelled]);

  const holidaysByDate = useMemo(() => {
    const map: Record<string, HolidayRow[]> = {};
    for (const h of holidays) {
      if (!map[h.holiday_date]) map[h.holiday_date] = [];
      map[h.holiday_date].push(h);
    }
    return map;
  }, [holidays]);

  const NO_VEHICLE_REQUIRED_CODES = useMemo(
    () => new Set(["TRAINING", "LEAVE", "SICK", "STANDBY", "OFFICE"]),
    []
  );

  const NON_WORKING_CODES = useMemo(
    () =>
      new Set([
        "LEAVE",
        "SICK",
        "UNAVAILABLE",
        "TIME_OFF",
        "TIMEOFF",
        "STANDBY",
        "TRAINING",
        "OFFICE",
        "DISPATCH_OPERATOR",
      ]),
    []
  );

  const isNonWorkingShift = useCallback(
    (s: ShiftRow): boolean => {
      if (s.counts_as_worked_hours === false) return true;
      const code = (s.shift_type_code || "").toString().trim().toUpperCase();
      if (!code) return false;
      return NON_WORKING_CODES.has(code);
    },
    [NON_WORKING_CODES]
  );

  // Looks up the real per-type flag (falling back to true/"is work" for an
  // unrecognised code) so a new/edited shift's own type can be passed into
  // work-time-rule validation -- a Leave/Sick/Training/Dispatch booking
  // shouldn't have max-shift-length/min-rest/cumulative-hours applied to it
  // either, not just be excluded from other shifts' calculations.
  const countsAsWorkedHoursForCode = useCallback(
    (code: string): boolean => shiftTypes.find((t) => t.code === code)?.counts_as_worked_hours ?? true,
    [shiftTypes]
  );

  // Backup/secondary check -- the hard block at save time is the real
  // enforcement (see handleSaveShift/handleDrop). This flags anything that
  // slipped through some other path, using only whatever shifts are already
  // loaded for the current view range, so the cumulative-hours rule (which
  // ideally wants a few weeks of lookback) may under-flag if the true cycle
  // start is outside that range -- the max-shift-length and min-rest checks
  // stay fully accurate regardless, since they only need adjacent shifts.
  // Non-working shifts (Leave/Sick/Training/Dispatch etc.) are excluded
  // entirely -- they don't have work-time rules applied to them, and they
  // must not contribute hours toward a real shift's duty period either.
  const violationsByShiftId = useMemo(() => {
    const byDriver = new Map<string, ShiftRow[]>();
    for (const s of shifts) {
      if (s.status === "cancelled") continue;
      if (isNonWorkingShift(s)) continue;
      if (!byDriver.has(s.driver_name)) byDriver.set(s.driver_name, []);
      byDriver.get(s.driver_name)!.push(s);
    }

    const map: Record<string, WorkTimeViolation[]> = {};
    for (const [, driverShifts] of byDriver) {
      const spans: ShiftSpan[] = driverShifts.map((s) => ({
        id: s.id,
        shift_date: s.shift_date,
        start_hhmm: s.start_hhmm,
        end_hhmm: s.end_hhmm,
        worked_hours: s.worked_hours || 0,
      }));

      for (const candidate of spans) {
        const others = spans.filter((s) => s.id !== candidate.id);
        const violations = checkWorkTimeRules(candidate, others, workTimeRules);
        if (violations.length > 0) map[candidate.id!] = violations;
      }
    }
    return map;
  }, [shifts, workTimeRules, isNonWorkingShift]);

  const rangeMissingVehicles = useMemo(
    () =>
      missingVehicles.filter((mv) => {
        if (mv.shift_date < rangeStartStr || mv.shift_date > rangeEndStr)
          return false;
        const code = (mv.shift_type_code || "").toString().trim().toUpperCase();
        if (NO_VEHICLE_REQUIRED_CODES.has(code)) return false;
        return true;
      }),
    [missingVehicles, rangeStartStr, rangeEndStr, NO_VEHICLE_REQUIRED_CODES]
  );

  type UnavailableVehicleRow = {
    key: string;
    shift_date: string;
    vehicle_number: string;
    vehicle_type: string | null;
    transmission_type?: string | null;
    reasons: string[];
    assignedShifts: ShiftRow[];
  };

  const rangeUnavailableVehicles = useMemo<UnavailableVehicleRow[]>(() => {
    const normaliseDate = (raw?: string | null): string | null => {
      if (!raw) return null;
      const s = raw.toString().trim();
      if (!s) return null;
      if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const parsed = new Date(s);
      if (isNaN(parsed.getTime())) return null;
      return format(parsed, "yyyy-MM-dd");
    };

    const rows: UnavailableVehicleRow[] = [];

    for (const day of rangeDays) {
      const dateStr = format(day, "yyyy-MM-dd");
      for (const v of vehicles) {
        const reasons: string[] = [];
        const status = (v.status || "").toString().trim().toLowerCase();

        if (status === "maintenance") reasons.push("Status: Maintenance");

        // Booked Maintenance window: only unavailable within [from, to].
        // Outside the window (e.g. before a maintenance booked in advance)
        // the vehicle remains available in the roster.
        const mFrom = normaliseDate(v.maintenance_from);
        const mTo = normaliseDate(v.maintenance_to);
        if (mFrom || mTo) {
          const windowStart = mFrom || mTo!;
          const windowEnd = mTo || mFrom!;
          if (dateStr >= windowStart && dateStr <= windowEnd) {
            const label =
              windowStart === windowEnd
                ? `Booked Maintenance (${format(
                    new Date(`${windowStart}T00:00:00`),
                    "d MMM"
                  )})`
                : `Booked Maintenance (${format(
                    new Date(`${windowStart}T00:00:00`),
                    "d MMM"
                  )} – ${format(new Date(`${windowEnd}T00:00:00`), "d MMM")})`;
            reasons.push(label);
          }
        }

        const cof = normaliseDate(v.cof_booked_for);
        if (cof && cof === dateStr) reasons.push("COF booked");

        const svc = normaliseDate(v.service_booked_for);
        if (svc && svc === dateStr) reasons.push("Service booked");

        const tyre = normaliseDate(v.tyre_rotation_booked_for);
        if (tyre && tyre === dateStr) reasons.push("Tyre rotation booked");


        if (reasons.length === 0) continue;

        const assignedShifts = shifts.filter(
          (s) =>
            s.shift_date === dateStr &&
            s.vehicle_number === v.vehicle_number &&
            s.status !== "cancelled"
        );

        rows.push({
          key: `${dateStr}-${v.vehicle_number}`,
          shift_date: dateStr,
          vehicle_number: v.vehicle_number,
          vehicle_type: v.vehicle_type,
          transmission_type: v.transmission_type,
          reasons,
          assignedShifts,
        });
      }
    }

    return rows;
  }, [vehicles, shifts, rangeDays]);

  const unavailableKeys = useMemo(() => {
    const set = new Set<string>();
    for (const u of rangeUnavailableVehicles) {
      set.add(`${u.shift_date}|${u.vehicle_number}`);
    }
    return set;
  }, [rangeUnavailableVehicles]);

  const rangeFreeVehicles = useMemo(
    () =>
      freeVehicles.filter(
        (fv) =>
          fv.shift_date >= rangeStartStr &&
          fv.shift_date <= rangeEndStr &&
          !unavailableKeys.has(`${fv.shift_date}|${fv.vehicle_number}`)
      ),
    [freeVehicles, rangeStartStr, rangeEndStr, unavailableKeys]
  );

  const rangeConflicts = useMemo(
    () =>
      bookingConflicts.filter(
        (bc) => bc.shift_date >= rangeStartStr && bc.shift_date <= rangeEndStr
      ),
    [bookingConflicts, rangeStartStr, rangeEndStr]
  );

  const rangeStats = useMemo(() => {
    const active = shifts.filter(
      (s) => s.status !== "cancelled" && s.status !== "completed"
    );
    return {
      total: active.length,
      totalHours: active.reduce((sum, s) => sum + (s.worked_hours || 0), 0),
      uniqueDrivers: new Set(active.map((s) => s.driver_name)).size,
      uniqueVehicles: new Set(
        active.filter((s) => s.vehicle_number).map((s) => s.vehicle_number)
      ).size,
      draftCount: active.filter((s) => s.status === "draft").length,
      publishedCount: active.filter((s) => s.status === "published").length,
    };
  }, [shifts]);

  // Raw per-(day, schedule_group) shift lists -- the shared source for both
  // the existing text-only allocation cards (computedAllocationGroups below)
  // and the richer "generate message" feature, which needs the real ShiftRow
  // objects (vehicle type, shift_type_code) to classify Sedan/SUV vs TMV vs
  // STS and pull each driver's jobs, not just pre-joined display text.
  const computedAllocationBuckets = useMemo(() => {
    const buckets = new Map<
      string,
      { shift_date: string; schedule_group: string; shifts: ShiftRow[] }
    >();

    for (const s of shifts) {
      if (s.status === "cancelled") continue;
      if (isNonWorkingShift(s)) continue;
      if (s.shift_date < rangeStartStr || s.shift_date > rangeEndStr) continue;

      const group =
        getShiftGroupValue(s) ||
        Array.from(driverGroupsMap[s.driver_name] || [])[0] ||
        "STANDARD";

      const key = `${s.shift_date}||${group}`;
      const existing = buckets.get(key);

      if (existing) {
        existing.shifts.push(s);
      } else {
        buckets.set(key, {
          shift_date: s.shift_date,
          schedule_group: group,
          shifts: [s],
        });
      }
    }

    for (const bucket of buckets.values()) {
      bucket.shifts.sort(
        (a, b) =>
          a.start_hhmm.localeCompare(b.start_hhmm) ||
          driverDisplayName(a).localeCompare(driverDisplayName(b))
      );
    }

    return Array.from(buckets.values()).sort(
      (a, b) =>
        a.shift_date.localeCompare(b.shift_date) ||
        a.schedule_group.localeCompare(b.schedule_group)
    );
  }, [shifts, rangeStartStr, rangeEndStr, isNonWorkingShift, driverGroupsMap]);

  const computedAllocationGroups = useMemo<AllocationGroupRow[]>(
    () =>
      computedAllocationBuckets.map((bucket) => ({
        shift_date: bucket.shift_date,
        schedule_group: bucket.schedule_group,
        grouped_text: bucket.shifts
          .map((s) => `${s.vehicle_number ? s.vehicle_number : "???"} - ${driverDisplayName(s)} (${s.start_hhmm}-${s.end_hhmm})`)
          .join("\n"),
      })),
    [computedAllocationBuckets]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [
        shiftsRes,
        driversRes,
        vehiclesRes,
        shiftTypesRes,
        holidaysRes,
        missingRes,
        freeRes,
        conflictsRes,
        allocRes,
        rosterGroupsRes,
        timeOffRes,
      ] = await Promise.all([
        supabase
          .from("vw_roster_shifts_enriched")
          .select("*")
          .gte("shift_date", rangeStartStr)
          .lte("shift_date", rangeEndStr),

        supabase
          .from("drivers")
          .select(
            "driver_name, driver_firstname, driver_lastname, is_active, can_drive_manual, roster_groups"
          )
          .eq("is_active", true)
          .order("driver_name"),

        supabase
          .from("vehicles")
          .select(
            "vehicle_number, vehicle_type, transmission_type, status, cof_booked_for, service_booked_for, tyre_rotation_booked_for, maintenance_from, maintenance_to"
          )
          .order("vehicle_number"),


        supabase
          .from("roster_shift_types")
          .select("*")
          .eq("is_active", true)
          .order("sort_order"),

        supabase
          .from("nz_holidays")
          .select("*")
          .gte("holiday_date", rangeStartStr)
          .lte("holiday_date", rangeEndStr),

        supabase
          .from("roster_missing_vehicles")
          .select("*")
          .gte("shift_date", rangeStartStr)
          .lte("shift_date", rangeEndStr),

        supabase
          .from("roster_free_vehicles")
          .select("*")
          .gte("shift_date", rangeStartStr)
          .lte("shift_date", rangeEndStr),

        supabase
          .from("roster_booking_conflicts")
          .select("*")
          .gte("shift_date", rangeStartStr)
          .lte("shift_date", rangeEndStr),

        supabase
          .from("roster_allocation_groups")
          .select("*")
          .gte("shift_date", rangeStartStr)
          .lte("shift_date", rangeEndStr),

        supabase
          .from("roster_driver_groups")
          .select("*")
          .order("sort_order", { ascending: true }),

        supabase
          .from("roster_time_off_requests")
          .select(
            "id, driver_name, request_type, start_date, end_date, start_time, end_time, status"
          )
          .eq("status", "approved")
          .lte("start_date", rangeEndStr)
          .gte("end_date", rangeStartStr),
      ]);

      if (shiftsRes.data) setShifts(shiftsRes.data as ShiftRow[]);
      if (driversRes.data) setDrivers(driversRes.data as DriverRow[]);
      if (vehiclesRes.data) setVehicles(vehiclesRes.data as VehicleRow[]);
      if (shiftTypesRes.data) setShiftTypes(shiftTypesRes.data as ShiftTypeRow[]);
      if (holidaysRes.data) setHolidays(holidaysRes.data as HolidayRow[]);
      if (missingRes.data) setMissingVehicles(missingRes.data as MissingVehicleRow[]);
      if (freeRes.data) setFreeVehicles(freeRes.data as FreeVehicleRow[]);
      if (conflictsRes.data) setBookingConflicts(conflictsRes.data as BookingConflictRow[]);
      if (allocRes.data) setAllocationGroups(allocRes.data as AllocationGroupRow[]);
      if (rosterGroupsRes.data) setRosterGroups(rosterGroupsRes.data as RosterGroupRow[]);
      if (timeOffRes.data) setApprovedTimeOff(timeOffRes.data as ApprovedTimeOffRow[]);
    } catch (err) {
      console.error("Error fetching roster data:", err);
      toast({
        title: "Error",
        description: "Failed to load roster data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [rangeStartStr, rangeEndStr]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const validIds = new Set(
      filteredShifts
        .filter((s) => s.status === "draft")
        .map((s) => s.id)
    );

    setSelectedShiftIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [filteredShifts]);

  const goBack = () => {
    if (viewMode === "day") setCurrentDate((d) => addDays(d, -1));
    else if (viewMode === "week") setCurrentDate((d) => addDays(d, -7));
    else setCurrentDate((d) => addMonths(d, -1));
  };

  const goForward = () => {
    if (viewMode === "day") setCurrentDate((d) => addDays(d, 1));
    else if (viewMode === "week") setCurrentDate((d) => addDays(d, 7));
    else setCurrentDate((d) => addMonths(d, 1));
  };

  const goToday = () => setCurrentDate(new Date());

  const handleDatePicked = (date: Date | undefined) => {
    if (!date) return;
    setCurrentDate(date);
    setDatePickerOpen(false);
  };

  const datePickerTitle = useMemo(() => {
    if (viewMode === "day") return `${format(currentDate, "EEEE")} • ${formatDate(currentDate)}`;
    if (viewMode === "week") {
      const ws = startOfWeek(currentDate, { weekStartsOn: NZ_WEEK_STARTS_ON });
      const we = endOfWeek(currentDate, { weekStartsOn: NZ_WEEK_STARTS_ON });
      return `${formatDate(ws)} – ${formatDate(we)}`;
    }
    // Spelled-out month/year, not formatDate -- see ActualPerformanceTab.tsx.
    return format(currentDate, "MMMM yyyy");
  }, [currentDate, viewMode]);

  const draftShiftsInRange = useMemo(
    () =>
      filteredShifts.filter(
        (s) =>
          s.status === "draft" &&
          s.shift_date >= rangeStartStr &&
          s.shift_date <= rangeEndStr &&
          (showCancelled || s.status !== "cancelled")
      ),
    [filteredShifts, rangeStartStr, rangeEndStr, showCancelled]
  );

  const selectedDraftCount = useMemo(() => {
    const selected = new Set(selectedShiftIds);
    return draftShiftsInRange.filter((s) => selected.has(s.id)).length;
  }, [draftShiftsInRange, selectedShiftIds]);

  const areAllDraftsSelected =
    draftShiftsInRange.length > 0 && selectedDraftCount === draftShiftsInRange.length;

  const toggleShiftSelected = (shiftId: string, checked: boolean) => {
    setSelectedShiftIds((prev) => {
      if (checked) return Array.from(new Set([...prev, shiftId]));
      return prev.filter((id) => id !== shiftId);
    });
  };

  const handleSelectAllDrafts = () => {
    const ids = draftShiftsInRange.map((s) => s.id);
    setSelectedShiftIds(ids);
  };

  const handleClearSelectedDrafts = () => {
    setSelectedShiftIds([]);
  };

  const openCreateDialog = (date?: Date) => {
    const d = date || currentDate;
    setFormData(defaultCreateShiftForm(d));
    setEditingShift(null);
    setCreateDialogOpen(true);
  };

  const loadSeriesCounts = (shift: ShiftRow) => {
    supabase
      .from("roster_shifts")
      .select("id", { count: "exact", head: true })
      .eq("repeat_group_id", shift.repeat_group_id as string)
      .then(({ count }) => {
        setSeriesShiftCount(count || 0);
      });
    supabase
      .from("roster_shifts")
      .select("id", { count: "exact", head: true })
      .eq("repeat_group_id", shift.repeat_group_id as string)
      .gte("shift_date", shift.shift_date)
      .then(({ count }) => {
        setFollowingShiftCount(count || 0);
      });
  };

  const openEditDialog = (shift: ShiftRow) => {
    if (shift.repeat_group_id) {
      setRecurringChoiceShift(shift);
      setRecurringChoiceAction("edit");
      loadSeriesCounts(shift);
      setRecurringChoiceOpen(true);
      return;
    }
    proceedToEditDialog(shift, "single");
  };

  const proceedToEditDialog = (shift: ShiftRow, mode: "single" | "following" | "series") => {
    setEditSeriesMode(mode);
    setEditingShift(shift);
    setFormData({
      driver_name: shift.driver_name,
      vehicle_number: shift.vehicle_number || "__NONE__",
      shift_type_code: shift.shift_type_code,
      status: shift.status === "draft" ? "draft" : "published",
      date: shift.shift_date,
      start_time: shift.start_hhmm,
      end_time: shift.end_hhmm,
      break_minutes: shift.break_minutes ?? 30,
      driver_visible_notes: shift.driver_visible_notes || "",
      admin_notes: shift.admin_notes || "",
      repeat_mode: "none",
      repeat_end_date: shift.shift_date,
      repeat_custom_days: [],
      repeat_cycle_days_on: 2,
      repeat_cycle_days_off: 2,
    });
    setCreateDialogOpen(true);
  };

  const handleSaveShift = async () => {
    if (!canEdit) return;
    if (!formData.driver_name || !formData.shift_type_code) {
      toast({
        title: "Validation",
        description: "Driver and shift type are required",
        variant: "destructive",
      });
      return;
    }

    const vehicleNum = formData.vehicle_number === "__NONE__" ? null : formData.vehicle_number;

    // Opt-in per shift type (Roster Positions -> "Vehicle Mandatory"), e.g.
    // Mail: the car has to be on the roster so the person and vehicle both
    // show as committed. Deliberately NOT tied to requires_vehicle, which CAR
    // and TMV shifts also carry but are routinely saved with no vehicle and
    // allocated on the day.
    const chosenShiftType = shiftTypes.find((t) => t.code === formData.shift_type_code);
    if (chosenShiftType?.vehicle_mandatory && !vehicleNum) {
      toast({
        title: "Vehicle required",
        description: `${chosenShiftType.label} shifts need a vehicle allocated so the car shows as committed.`,
        variant: "destructive",
      });
      return;
    }

    if (!editingShift && formData.repeat_mode !== "none") {
      const dates = generateRepeatDates(
        formData.repeat_mode,
        formData.date,
        formData.repeat_end_date,
        formData.repeat_custom_days,
        formData.repeat_cycle_days_on,
        formData.repeat_cycle_days_off
      );

      if (dates.length === 0) {
        toast({
          title: "No dates",
          description: "No dates match the repeat criteria",
          variant: "destructive",
        });
        return;
      }

      if (vehicleNum) {
        try {
          const conflicts = await findVehicleConflicts(vehicleNum, dates, formData.start_time, formData.end_time);
          if (conflicts.length > 0) {
            const first = conflicts[0];
            toast({
              title: "Vehicle already assigned",
              description:
                conflicts.length === 1
                  ? `${vehicleNum} is already assigned to ${first.driver_name} on ${first.shift_date} (${formatTime12(first.start_hhmm)}–${formatTime12(first.end_hhmm)}).`
                  : `${vehicleNum} is already assigned to another driver on ${conflicts.length} of these dates -- starting ${first.shift_date} (${first.driver_name}).`,
              variant: "destructive",
            });
            return;
          }
        } catch (err: any) {
          toast({
            title: "Error",
            description: err.message || "Failed to check vehicle availability",
            variant: "destructive",
          });
          return;
        }
      }

      try {
        const rules = await loadWorkTimeRules();
        const candidates: ShiftSpan[] = dates.map((d) => ({
          shift_date: d,
          start_hhmm: formData.start_time,
          end_hhmm: formData.end_time,
          worked_hours: calcWorkedHours(formData.start_time, formData.end_time, formData.break_minutes),
          counts_as_worked_hours: countsAsWorkedHoursForCode(formData.shift_type_code),
        }));
        const batchResults = await validateShiftBatch(formData.driver_name, candidates, rules);
        const breaching = batchResults.filter((r) => r.violations.length > 0);
        if (breaching.length > 0) {
          const first = breaching[0];
          toast({
            title: "Breaches Work Time Rules",
            description:
              breaching.length === 1
                ? `${first.candidate.shift_date}: ${first.violations.map((v) => v.message).join(" ")}`
                : `${breaching.length} of these dates breach Work Time Rules -- starting ${first.candidate.shift_date}: ${first.violations[0].message}`,
            variant: "destructive",
          });
          return;
        }
      } catch (err) {
        toast({
          title: "Error",
          description: err instanceof Error ? err.message : "Failed to validate work time rules",
          variant: "destructive",
        });
        return;
      }

      setRepeatDates(dates);
      setRepeatConfirmOpen(true);
      return;
    }

    // Series edits check against the series' own dates inside saveSingleShift,
    // once it has fetched which dates that actually covers.
    if (vehicleNum && !(editingShift && editSeriesMode !== "single")) {
      try {
        const conflicts = await findVehicleConflicts(
          vehicleNum,
          [formData.date],
          formData.start_time,
          formData.end_time,
          editingShift ? [editingShift.id] : []
        );
        if (conflicts.length > 0) {
          const first = conflicts[0];
          toast({
            title: "Vehicle already assigned",
            description: `${vehicleNum} is already assigned to ${first.driver_name} on ${formData.date} (${formatTime12(first.start_hhmm)}–${formatTime12(first.end_hhmm)}).`,
            variant: "destructive",
          });
          return;
        }
      } catch (err: any) {
        toast({
          title: "Error",
          description: err.message || "Failed to check vehicle availability",
          variant: "destructive",
        });
        return;
      }
    }

    await saveSingleShift();
  };

  const saveSingleShift = async () => {
    setSaving(true);
    try {
      const vehicleNum =
        formData.vehicle_number === "__NONE__" ? null : formData.vehicle_number;
      const workedHours = calcWorkedHours(
        formData.start_time,
        formData.end_time,
        formData.break_minutes
      );

      const commonPayload = {
        driver_name: formData.driver_name,
        vehicle_number: vehicleNum,
        shift_type_code: formData.shift_type_code,
        status: formData.status,
        break_minutes: formData.break_minutes,
        start_hhmm: formData.start_time,
        end_hhmm: formData.end_time,
        worked_hours: workedHours,
        driver_visible_notes: formData.driver_visible_notes || null,
        admin_notes: formData.admin_notes || null,
        updated_at: new Date().toISOString(),
      };

      const isSeriesEdit = !!(editingShift && editSeriesMode !== "single" && editingShift.repeat_group_id);

      if (!isSeriesEdit) {
        const rules = await loadWorkTimeRules();
        const candidate: ShiftSpan = {
          shift_date: formData.date,
          start_hhmm: formData.start_time,
          end_hhmm: formData.end_time,
          worked_hours: workedHours,
          counts_as_worked_hours: countsAsWorkedHoursForCode(formData.shift_type_code),
        };
        const violations = await validateShift(formData.driver_name, candidate, rules, editingShift?.id);
        if (violations.length > 0) {
          toast({
            title: "Breaches Work Time Rules",
            description: violations.map((v) => v.message).join(" "),
            variant: "destructive",
          });
          return;
        }
      }

      if (editingShift) {
        if (editSeriesMode !== "single" && editingShift.repeat_group_id) {
          // "This and following" only touches the occurrence being edited and the ones after
          // it, so shifts that have already been worked (and paid) are never rewritten or
          // checked for conflicts. "All" really is every shift in the series.
          let seriesQuery = supabase
            .from("roster_shifts")
            .select("id, shift_date")
            .eq("repeat_group_id", editingShift.repeat_group_id);
          if (editSeriesMode === "following") seriesQuery = seriesQuery.gte("shift_date", editingShift.shift_date);
          const { data: seriesShifts, error: fetchErr } = await seriesQuery;

          if (fetchErr) throw fetchErr;

          if (seriesShifts && seriesShifts.length > 0) {
            const rules = await loadWorkTimeRules();
            const candidates: ShiftSpan[] = seriesShifts.map((s) => ({
              shift_date: s.shift_date,
              start_hhmm: formData.start_time,
              end_hhmm: formData.end_time,
              worked_hours: workedHours,
              counts_as_worked_hours: countsAsWorkedHoursForCode(formData.shift_type_code),
            }));
            const seriesShiftIds = seriesShifts.map((s) => s.id);
            const batchResults = await validateShiftBatch(formData.driver_name, candidates, rules, seriesShiftIds);
            const breaching = batchResults.filter((r) => r.violations.length > 0);
            if (breaching.length > 0) {
              const first = breaching[0];
              toast({
                title: "Breaches Work Time Rules",
                description:
                  breaching.length === 1
                    ? `${first.candidate.shift_date}: ${first.violations.map((v) => v.message).join(" ")}`
                    : `${breaching.length} date(s) in this series breach Work Time Rules -- starting ${first.candidate.shift_date}: ${first.violations[0].message}`,
                variant: "destructive",
              });
              return;
            }

            if (vehicleNum) {
              const conflicts = await findVehicleConflicts(
                vehicleNum,
                seriesShifts.map((s) => s.shift_date),
                formData.start_time,
                formData.end_time,
                seriesShifts.map((s) => s.id)
              );
              if (conflicts.length > 0) {
                const first = conflicts[0];
                toast({
                  title: "Vehicle already assigned",
                  description: `${vehicleNum} is already assigned to another driver on ${conflicts.length} date(s) in this series -- starting ${first.shift_date} (${first.driver_name}).`,
                  variant: "destructive",
                });
                return;
              }
            }

            for (let i = 0; i < seriesShifts.length; i += 50) {
              const batch = seriesShifts.slice(i, i + 50);

              for (const s of batch) {
                await supabase
                  .from("roster_shifts")
                  .update({
                    ...commonPayload,
                    shift_date: s.shift_date,
                    start_at: combineDateAndTime(s.shift_date, formData.start_time),
                    end_at: combineDateAndTime(s.shift_date, formData.end_time),
                  })
                  .eq("id", s.id);
              }
            }

            toast({
              title: "Series Updated",
              description:
                editSeriesMode === "following"
                  ? `${seriesShifts.length} shifts updated (this one and the ones after it)`
                  : `${seriesShifts.length} shifts in the series updated`,
            });
          }
        } else {
          const singlePayload: Record<string, any> = {
            ...commonPayload,
            shift_date: formData.date,
            start_at: combineDateAndTime(formData.date, formData.start_time),
            end_at: combineDateAndTime(formData.date, formData.end_time),
          };

          if (editingShift.repeat_group_id) {
            singlePayload.repeat_group_id = null;
          }

          const { error } = await supabase
            .from("roster_shifts")
            .update(singlePayload)
            .eq("id", editingShift.id);

          if (error) throw error;

          toast({
            title: "Updated",
            description: "Shift updated successfully",
          });
        }
      } else {
        const payload = {
          ...commonPayload,
          shift_date: formData.date,
          start_at: combineDateAndTime(formData.date, formData.start_time),
          end_at: combineDateAndTime(formData.date, formData.end_time),
          created_at: new Date().toISOString(),
        };

        const { error } = await supabase.from("roster_shifts").insert(payload);
        if (error) throw error;

        toast({
          title: "Created",
          description: "Shift created successfully",
        });
      }

      // Editing an existing shift stays open (per feedback: saving
      // shouldn't close the dialog) -- editingShift/editSeriesMode are left
      // as-is so a further save keeps updating the same shift. A brand-new
      // shift still closes: editingShift is still null after create, so
      // staying open would insert another new shift on the next save
      // instead of updating this one.
      if (!editingShift) {
        setCreateDialogOpen(false);
        setEditingShift(null);
        setEditSeriesMode("single");
      }
      fetchAll();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to save shift",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRepeatConfirm = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const vehicleNum =
        formData.vehicle_number === "__NONE__" ? null : formData.vehicle_number;
      const workedHours = calcWorkedHours(
        formData.start_time,
        formData.end_time,
        formData.break_minutes
      );
      const repeatGroupId = crypto.randomUUID();

      const repeatGroupPayload: Record<string, any> = {
        id: repeatGroupId,
        name: `${formData.driver_name} – ${formData.shift_type_code} (${formData.repeat_mode})`,
        source_type: "shift",
        repeat_pattern: formData.repeat_mode,
        interval_count: formData.repeat_mode === "fortnightly" ? 2 : 1,
        start_date: formData.date,
        end_date: formData.repeat_end_date,
        is_active: true,
      };

      if (formData.repeat_mode === "custom" && formData.repeat_custom_days.length > 0) {
        repeatGroupPayload.days_of_week = formData.repeat_custom_days;
      }

      if (formData.repeat_mode === "cycle") {
        repeatGroupPayload.cycle_days_on = formData.repeat_cycle_days_on;
        repeatGroupPayload.cycle_days_off = formData.repeat_cycle_days_off;
      }

      const { error: groupError } = await supabase
        .from("roster_repeat_groups")
        .insert(repeatGroupPayload);

      if (groupError) throw groupError;

      const rows = repeatDates.map((dateStr) => ({
        driver_name: formData.driver_name,
        vehicle_number: vehicleNum,
        shift_type_code: formData.shift_type_code,
        status: formData.status,
        start_at: combineDateAndTime(dateStr, formData.start_time),
        end_at: combineDateAndTime(dateStr, formData.end_time),
        break_minutes: formData.break_minutes,
        shift_date: dateStr,
        start_hhmm: formData.start_time,
        end_hhmm: formData.end_time,
        worked_hours: workedHours,
        driver_visible_notes: formData.driver_visible_notes || null,
        admin_notes: formData.admin_notes || null,
        repeat_group_id: repeatGroupId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));

      for (let i = 0; i < rows.length; i += 100) {
        const batch = rows.slice(i, i + 100);
        const { error } = await supabase.from("roster_shifts").insert(batch);
        if (error) throw error;
      }

      toast({
        title: "Created",
        description: `${rows.length} recurring shifts created successfully`,
      });

      setRepeatConfirmOpen(false);
      setCreateDialogOpen(false);
      setEditingShift(null);
      fetchAll();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to create recurring shifts",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShift = async (shiftId: string) => {
    if (!canEdit) return;
    const shift = shifts.find((s) => s.id === shiftId);
    if (shift?.repeat_group_id) {
      setRecurringChoiceShift(shift);
      setRecurringChoiceAction("delete");
      loadSeriesCounts(shift);
      setRecurringChoiceOpen(true);
      return;
    }

    if (!confirm("Are you sure you want to delete this shift?")) return;

    try {
      const { error } = await supabase
        .from("roster_shifts")
        .delete()
        .eq("id", shiftId);

      if (error) throw error;

      toast({ title: "Deleted", description: "Shift deleted" });
      fetchAll();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete shift",
        variant: "destructive",
      });
    }
  };

  const handleDeleteSingleFromSeries = async (shiftId: string) => {
    try {
      const { error } = await supabase
        .from("roster_shifts")
        .delete()
        .eq("id", shiftId);

      if (error) throw error;

      toast({
        title: "Deleted",
        description: "Single shift removed from series",
      });
      fetchAll();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete shift",
        variant: "destructive",
      });
    }
  };

  // Deletes this occurrence and every later one in its series, leaving earlier shifts alone.
  const handleDeleteFollowingInSeries = async (shift: ShiftRow) => {
    if (!shift.repeat_group_id) return;
    try {
      const { error } = await supabase
        .from("roster_shifts")
        .delete()
        .eq("repeat_group_id", shift.repeat_group_id)
        .gte("shift_date", shift.shift_date);

      if (error) throw error;

      toast({
        title: "Shifts Deleted",
        description: "This shift and the ones after it in the series have been deleted",
      });
      fetchAll();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete shifts",
        variant: "destructive",
      });
    }
  };

  const handleDeleteEntireSeries = async (repeatGroupId: string) => {
    try {
      const { error } = await supabase
        .from("roster_shifts")
        .delete()
        .eq("repeat_group_id", repeatGroupId);

      if (error) throw error;

      await supabase.from("roster_repeat_groups").delete().eq("id", repeatGroupId);

      toast({
        title: "Series Deleted",
        description: "All shifts in the series have been deleted",
      });

      fetchAll();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete series",
        variant: "destructive",
      });
    }
  };

  const handleCancelShift = async (shiftId: string) => {
    try {
      const { error } = await supabase
        .from("roster_shifts")
        .update({ status: "cancelled", updated_at: new Date().toISOString() })
        .eq("id", shiftId);

      if (error) throw error;

      toast({ title: "Cancelled", description: "Shift cancelled" });
      fetchAll();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to cancel shift",
        variant: "destructive",
      });
    }
  };

  const shiftOverlapsApprovedTimeOff = useCallback(
    (shift: ShiftRow): ApprovedTimeOffRow | null => {
      for (const req of approvedTimeOff) {
        if (req.driver_name !== shift.driver_name) continue;
        if (shift.shift_date < req.start_date || shift.shift_date > req.end_date)
          continue;

        if (req.request_type === "partial_day") {
          const reqStart = req.start_time || "00:00";
          const reqEnd = req.end_time || "23:59";

          if (shift.start_hhmm < reqEnd && shift.end_hhmm > reqStart) return req;
        } else {
          return req;
        }
      }
      return null;
    },
    [approvedTimeOff]
  );

  const buildPublishConflicts = useCallback(
    (scope: { kind: "day" | "all" | "selected"; dateStr?: string }) => {
      const selectedSet = new Set(selectedShiftIds);

      const targetShifts = shifts.filter((s) => {
        if (s.status !== "draft") return false;

        if (scope.kind === "day") return s.shift_date === scope.dateStr;
        if (scope.kind === "selected") return selectedSet.has(s.id);

        return s.shift_date >= rangeStartStr && s.shift_date <= rangeEndStr;
      });

      const out: PublishConflict[] = [];

      for (const s of targetShifts) {
        const reasons: string[] = [];
        let hasVehicle = false;
        let hasDriver = false;

        if (s.vehicle_number) {
          const key = `${s.shift_date}|${s.vehicle_number}`;
          if (unavailableKeys.has(key)) {
            const uv = rangeUnavailableVehicles.find(
              (u) => u.shift_date === s.shift_date && u.vehicle_number === s.vehicle_number
            );
            reasons.push(
              `Vehicle ${s.vehicle_number} is unavailable (${uv?.reasons.join(", ") || "booked"})`
            );
            hasVehicle = true;
          }
        }

        const timeOff = shiftOverlapsApprovedTimeOff(s);
        if (timeOff) {
          const when =
            timeOff.request_type === "partial_day" &&
            timeOff.start_time &&
            timeOff.end_time
              ? ` (${timeOff.start_time}–${timeOff.end_time})`
              : "";
          reasons.push(`Driver has approved time off${when}`);
          hasDriver = true;
        }

        if (reasons.length > 0) {
          out.push({
            shift: s,
            reasons,
            kind: hasVehicle && hasDriver ? "both" : hasVehicle ? "vehicle" : "driver",
          });
        }
      }

      return out;
    },
    [
      shifts,
      rangeStartStr,
      rangeEndStr,
      unavailableKeys,
      rangeUnavailableVehicles,
      shiftOverlapsApprovedTimeOff,
      selectedShiftIds,
    ]
  );

  const doPublish = useCallback(
    async (scope: { kind: "day" | "all" | "selected"; dateStr?: string }) => {
      setPublishing(true);

      try {
        let targetIds: string[] | null = null;

        if (scope.kind === "selected") {
          targetIds = selectedShiftIds;
          if (targetIds.length === 0) {
            toast({
              title: "Nothing selected",
              description: "Select at least one draft shift first",
              variant: "destructive",
            });
            return;
          }
        }

        if (scope.kind === "selected" && targetIds) {
          const { error } = await supabase
            .from("roster_shifts")
            .update({
              status: "published",
              published_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .in("id", targetIds)
            .eq("status", "draft");

          if (error) throw error;

          toast({
            title: "Published",
            description: `${targetIds.length} selected draft shift${
              targetIds.length === 1 ? "" : "s"
            } published`,
          });

          setSelectedShiftIds((prev) => prev.filter((id) => !targetIds!.includes(id)));
        } else {
          let query = supabase
            .from("roster_shifts")
            .update({
              status: "published",
              published_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq("status", "draft");

          if (scope.kind === "day" && scope.dateStr) {
            query = query.eq("shift_date", scope.dateStr);
          } else {
            query = query.gte("shift_date", rangeStartStr).lte("shift_date", rangeEndStr);
          }

          const { error } = await query;
          if (error) throw error;

          toast({
            title: "Published",
            description:
              scope.kind === "day" && scope.dateStr
                ? `Drafts for ${format(
                    new Date(`${scope.dateStr}T00:00:00`),
                    "EEE d MMM"
                  )} published`
                : "All draft shifts published",
          });
        }

        setPublishValidationOpen(false);
        fetchAll();
      } catch (err: any) {
        toast({
          title: "Error",
          description: err.message,
          variant: "destructive",
        });
      } finally {
        setPublishing(false);
      }
    },
    [rangeStartStr, rangeEndStr, fetchAll, selectedShiftIds]
  );

  const handlePublishDay = async (dateStr: string) => {
    const conflicts = buildPublishConflicts({ kind: "day", dateStr });
    if (conflicts.length > 0) {
      setPublishConflicts(conflicts);
      setPublishScope({ kind: "day", dateStr });
      setPublishValidationOpen(true);
      return;
    }
    await doPublish({ kind: "day", dateStr });
  };

  const handlePublishAll = async () => {
    const conflicts = buildPublishConflicts({ kind: "all" });
    if (conflicts.length > 0) {
      setPublishConflicts(conflicts);
      setPublishScope({ kind: "all" });
      setPublishValidationOpen(true);
      return;
    }
    await doPublish({ kind: "all" });
  };

  const handlePublishSelected = async () => {
    if (selectedShiftIds.length === 0) {
      toast({
        title: "Nothing selected",
        description: "Tick the draft shifts you want to publish first",
        variant: "destructive",
      });
      return;
    }

    const conflicts = buildPublishConflicts({ kind: "selected" });
    if (conflicts.length > 0) {
      setPublishConflicts(conflicts);
      setPublishScope({ kind: "selected" });
      setPublishValidationOpen(true);
      return;
    }

    await doPublish({ kind: "selected" });
  };

  const handleOpenFindCover = (shift: ShiftRow) => {
    setFindCoverShift(shift);
    setFindCoverOpen(true);
  };

  const handleOpenTrendOverlay = (dateStr: string) => {
    setTrendOverlayDate(dateStr);
    setTrendOverlayOpen(true);
  };

  const handleCopyAllocation = (dateStr: string) => {
    const dayGroups = computedAllocationGroups.filter((g) => g.shift_date === dateStr);

    if (dayGroups.length === 0) {
      toast({
        title: "Nothing to copy",
        description: "No working shifts found for this day",
      });
      return;
    }

    const text = dayGroups
      .map((g) => `--- ${g.schedule_group} ---\n${g.grouped_text}`)
      .join("\n\n");

    navigator.clipboard.writeText(text).then(() => {
      toast({ title: "Copied", description: "Allocation copied to clipboard" });
    });
  };

  const handleDragOver = (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverDate(dateStr);
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = async (e: React.DragEvent, targetDate: string) => {
    e.preventDefault();
    setDragOverDate(null);
    if (!canEdit) return;

    try {
      const raw = e.dataTransfer.getData("application/json");
      if (!raw) return;

      const dragData: DragData = JSON.parse(raw);

      if (dragData.sourceDate === targetDate) return;

      const shift = shifts.find((s) => s.id === dragData.shiftId);
      if (!shift) return;

      const workedHours = calcWorkedHours(
        shift.start_hhmm,
        shift.end_hhmm,
        shift.break_minutes
      );

      const rules = await loadWorkTimeRules();
      const candidate: ShiftSpan = {
        shift_date: targetDate,
        start_hhmm: shift.start_hhmm,
        end_hhmm: shift.end_hhmm,
        worked_hours: workedHours,
        counts_as_worked_hours: shift.counts_as_worked_hours,
      };
      const violations = await validateShift(shift.driver_name, candidate, rules, shift.id);
      if (violations.length > 0) {
        toast({
          title: "Breaches Work Time Rules",
          description: violations.map((v) => v.message).join(" "),
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from("roster_shifts")
        .update({
          shift_date: targetDate,
          start_at: combineDateAndTime(targetDate, shift.start_hhmm),
          end_at: combineDateAndTime(targetDate, shift.end_hhmm),
          worked_hours: workedHours,
          updated_at: new Date().toISOString(),
        })
        .eq("id", dragData.shiftId);

      if (error) throw error;

      toast({
        title: "Moved",
        description: `Shift moved to ${format(
          new Date(`${targetDate}T00:00:00`),
          "EEE d MMM"
        )}`,
      });

      fetchAll();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to move shift",
        variant: "destructive",
      });
    }
  };

  const isToday = (date: Date) => isSameDay(date, new Date());

  const getVehicleTypeIcon = (vehicleType?: string | null) => {
    if (getIconStyle(vehicleType, vehicleTypes) === "truck") {
      return <Truck className="h-4 w-4" />;
    }
    return <Car className="h-4 w-4" />;
  };

  const renderHolidayBanner = (dateStr: string, compact = false) => {
    const dayHolidays = holidaysByDate[dateStr] || [];
    if (dayHolidays.length === 0) return null;

    return (
      <div
        className={`bg-orange-500/20 border-b border-orange-500/40 ${
          compact ? "px-1 py-0.5" : "px-2 py-1"
        }`}
      >
        <div
          className={`${
            compact ? "text-[9px]" : "text-[10px]"
          } font-semibold uppercase tracking-wider text-orange-300 truncate`}
        >
          {dayHolidays.map((h) => h.holiday_name).join(" • ")}
        </div>
      </div>
    );
  };

  const renderDayColumn = (day: Date, wide = false) => {
    const dateStr = format(day, "yyyy-MM-dd");
    const dayShifts = filteredShiftsByDate[dateStr] || [];
    const dayHolidays = holidaysByDate[dateStr] || [];
    const hasDrafts = dayShifts.some((s) => s.status === "draft");
    const today = isToday(day);
    const isDragOver = dragOverDate === dateStr;
    const hasHoliday = dayHolidays.length > 0;

    return (
      <div
        key={dateStr}
        className={`rounded-lg border min-h-[200px] flex flex-col transition-all ${
          isDragOver
            ? "border-blue-400 bg-blue-900/30 ring-2 ring-blue-400/50"
            : hasHoliday
            ? "border-orange-500/50 bg-orange-950/20"
            : today
            ? "border-blue-500 bg-blue-950/20"
            : "border-gray-700 bg-gray-800/40"
        }`}
        onDragOver={(e) => handleDragOver(e, dateStr)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, dateStr)}
      >
        {renderHolidayBanner(dateStr)}

        <div
          className={`p-2 border-b flex items-center justify-between ${
            today ? "border-blue-500/50 bg-blue-900/30" : "border-gray-700/50"
          }`}
        >
          <div className="flex items-center gap-1 min-w-0">
            <span className={`text-xs font-medium ${today ? "text-blue-400" : "text-gray-400"}`}>
              {format(day, "EEE")}
            </span>
            <span className={`text-sm font-bold ${today ? "text-blue-300" : "text-white"}`}>
              {format(day, "d")}
            </span>
            {wide && <span className="text-xs text-gray-500 ml-1">{format(day, "MMM")}</span>}
          </div>

          <div className="flex items-center gap-0.5 shrink-0">
            {hasDrafts && (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 text-green-400 hover:text-green-300"
                title="Publish day"
                onClick={() => handlePublishDay(dateStr)}
              >
                <Eye className="h-3 w-3" />
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 text-gray-400 hover:text-blue-400"
              title="Copy allocation"
              onClick={() => handleCopyAllocation(dateStr)}
            >
              <Copy className="h-3 w-3" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 text-cyan-400 hover:text-cyan-300"
              title="Show trends"
              onClick={() => handleOpenTrendOverlay(dateStr)}
            >
              <span className="text-[10px] font-bold">T</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 text-gray-400 hover:text-blue-400"
              title="Add shift"
              onClick={() => openCreateDialog(day)}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </div>

        <div className={`p-1 flex-1 overflow-y-auto space-y-1 ${isDragOver ? "bg-blue-900/10" : ""}`}>
          {dayShifts.length === 0 && (
            <p className="text-xs text-gray-600 text-center mt-4">No shifts</p>
          )}

          {dayShifts.map((shift) => (
            <ShiftCard
              key={shift.id}
              shift={shift}
              onEdit={openEditDialog}
              compact={viewMode === "month"}
              selectable={shift.status === "draft"}
              selected={selectedShiftIds.includes(shift.id)}
              onSelectedChange={(checked) => toggleShiftSelected(shift.id, checked)}
            />
          ))}
        </div>

        <div className="p-1 border-t border-gray-700/50 text-center">
          <span className="text-[10px] text-gray-500">
            {dayShifts.length} shift{dayShifts.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-lg text-gray-400">Loading roster...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-blue-400" />
            Roster
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            {formatDate(rangeStart)} – {formatDate(rangeEnd)}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-gray-800 border border-gray-700 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode("day")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                viewMode === "day" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <Calendar className="h-3.5 w-3.5" /> Day
            </button>
            <button
              onClick={() => setViewMode("week")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                viewMode === "week" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <CalendarRange className="h-3.5 w-3.5" /> Week
            </button>
            <button
              onClick={() => setViewMode("month")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                viewMode === "month" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Month
            </button>
          </div>

          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="min-w-[210px] justify-between">
                <span>{datePickerTitle}</span>
                <ChevronsUpDown className="h-4 w-4 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3 bg-gray-900 border-gray-700" align="end">
              <div className="mb-2 text-sm font-medium text-white">
                {viewMode === "day"
                  ? "Choose a day"
                  : viewMode === "week"
                  ? "Choose any day in the week"
                  : "Choose a date in the month"}
              </div>
              <UiCalendar
                mode="single"
                selected={currentDate}
                onSelect={handleDatePicked}
                initialFocus
                weekStartsOn={NZ_WEEK_STARTS_ON}
              />
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="sm" onClick={goBack}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goForward}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={fetchAll}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => openCreateDialog()}>
            <Plus className="h-4 w-4 mr-1" /> New Shift
          </Button>

          {rangeStats.draftCount > 0 && (
            <Button
              size="sm"
              variant="default"
              onClick={handlePublishAll}
              className="bg-green-600 hover:bg-green-700"
            >
              <Eye className="h-4 w-4 mr-1" /> Publish All ({rangeStats.draftCount})
            </Button>
          )}
        </div>
      </div>

      {(rangeMissingVehicles.length > 0 ||
        rangeFreeVehicles.length > 0 ||
        rangeUnavailableVehicles.length > 0 ||
        rangeConflicts.length > 0) && (
        <div
          className={`grid grid-cols-1 gap-3 ${
            rangeUnavailableVehicles.length > 0 ? "lg:grid-cols-4" : "lg:grid-cols-3"
          }`}
        >
          {rangeMissingVehicles.length > 0 && (
            <Card className="bg-yellow-900/30 border-yellow-700/50">
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-sm text-yellow-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Missing Vehicles ({rangeMissingVehicles.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-1">
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {rangeMissingVehicles.map((mv) => (
                    <div
                      key={mv.id}
                      className="text-xs text-yellow-300 flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        {format(new Date(`${mv.shift_date}T00:00:00`), "EEE d")} — {mv.driver_name} (
                        {mv.shift_type_label})
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-yellow-400 hover:text-yellow-200 shrink-0"
                        onClick={() => openEditDialog(mv)}
                      >
                        Assign
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {rangeFreeVehicles.length > 0 && (
            <Card className="bg-green-900/30 border-green-700/50">
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-sm text-green-400 flex items-center gap-2">
                  <Car className="h-4 w-4" /> Free Vehicles ({rangeFreeVehicles.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-1">
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {rangeFreeVehicles.map((fv, idx) => (
                    <div
                      key={`${fv.shift_date}-${fv.vehicle_number}-${idx}`}
                      className="text-xs text-green-300 flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        {format(new Date(`${fv.shift_date}T00:00:00`), "EEE d")} — {fv.vehicle_number}
                        {fv.vehicle_type ? ` (${fv.vehicle_type})` : ""}
                      </span>
                      {fv.usual_driver_name && (
                        <span className="text-[10px] text-green-500 shrink-0">
                          usual: {fv.usual_driver_name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {rangeUnavailableVehicles.length > 0 && (
            <Card className="bg-orange-900/30 border-orange-700/50">
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-sm text-orange-400 flex items-center gap-2">
                  <Wrench className="h-4 w-4" /> Unavailable Vehicles ({rangeUnavailableVehicles.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-1">
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {rangeUnavailableVehicles.map((uv) => {
                    const hasAssigned = uv.assignedShifts.length > 0;

                    return (
                      <div
                        key={uv.key}
                        className={`text-xs rounded-md px-2 py-1.5 border ${
                          hasAssigned
                            ? "bg-red-950/40 border-red-700/50 text-red-200"
                            : "bg-orange-950/30 border-orange-700/40 text-orange-200"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5 truncate font-medium">
                            <Wrench className="h-3 w-3 shrink-0" />
                            {format(new Date(`${uv.shift_date}T00:00:00`), "EEE d")} — {uv.vehicle_number}
                            {uv.vehicle_type ? ` (${uv.vehicle_type})` : ""}
                          </span>

                          {hasAssigned && (
                            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-300">
                              <AlertTriangle className="h-3 w-3" /> Swap needed
                            </span>
                          )}
                        </div>

                        <div
                          className={`text-[10px] mt-0.5 ${
                            hasAssigned ? "text-red-300/90" : "text-orange-300/80"
                          }`}
                        >
                          {uv.reasons.join(" • ")}
                        </div>

                        {hasAssigned && (
                          <div className="mt-1 space-y-0.5">
                            {uv.assignedShifts.map((s) => (
                              <div key={s.id} className="flex items-center justify-between gap-2">
                                <span className="text-[11px] text-red-100 truncate">
                                  Assigned to {driverDisplayName(s)} ({s.start_hhmm}–{s.end_hhmm})
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 px-2 text-red-300 hover:text-red-100 shrink-0"
                                  onClick={() => openEditDialog(s)}
                                >
                                  Reassign
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {rangeConflicts.length > 0 && (
            <Card className="bg-red-900/30 border-red-700/50">
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-sm text-red-400 flex items-center gap-2">
                  <Wrench className="h-4 w-4" /> Booking Conflicts ({rangeConflicts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-1">
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {rangeConflicts.map((bc, i) => (
                    <div key={i} className="text-xs text-red-300">
                      {format(new Date(`${bc.shift_date}T00:00:00`), "EEE d")} — Vehicle {bc.vehicle_number} has{" "}
                      {bc.booking_type} booked ({bc.driver_name})
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800 border border-gray-700">
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          {/* Demand and Allocation are fleet-wide dispatch/forecasting
              tools (job demand vs. driver supply, night-before broadcast
              messages to schedule groups) with no per-driver scope --
              hidden for a restricted user, same as Fleet Pulse/Fleet
              Capacity/IVR Calls. */}
          {!scopedDriverNames && <TabsTrigger value="demand">Demand</TabsTrigger>}
          <TabsTrigger value="list">List View</TabsTrigger>
          {!scopedDriverNames && <TabsTrigger value="allocation">Allocation</TabsTrigger>}
          <TabsTrigger value="swaps" className="flex items-center gap-1.5">
            <ArrowLeftRight className="h-3.5 w-3.5" />
            Swaps
          </TabsTrigger>
          <TabsTrigger value="timeoff" className="flex items-center gap-1.5">
            <CalendarOff className="h-3.5 w-3.5" />
            Time Off
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4 space-y-4">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-3">
              <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <RosterFilterBar
                    shiftTypes={shiftTypes}
                    drivers={drivers}
                    shifts={shifts}
                    rosterGroups={rosterGroups}
                    filters={rosterFilters}
                    onFiltersChange={setRosterFilters}
                  />
                </div>

                <div className="flex flex-col sm:flex-row xl:flex-col gap-2 xl:min-w-[250px]">
                  <Button
                    variant={showCoverage ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowCoverage((prev) => !prev)}
                    className={showCoverage ? "bg-blue-600 hover:bg-blue-700" : ""}
                  >
                    <Rows3 className="h-4 w-4 mr-1" />
                    Coverage {showCoverage ? "On" : "Off"}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={areAllDraftsSelected ? handleClearSelectedDrafts : handleSelectAllDrafts}
                  >
                    {areAllDraftsSelected ? (
                      <>
                        <Square className="h-4 w-4 mr-1" />
                        Clear Selected
                      </>
                    ) : (
                      <>
                        <CheckSquare className="h-4 w-4 mr-1" />
                        Select All Drafts
                      </>
                    )}
                  </Button>

                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handlePublishSelected}
                    disabled={selectedDraftCount === 0}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Publish Selected ({selectedDraftCount})
                  </Button>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-blue-500 text-blue-300">
                  Drafts in view: {draftShiftsInRange.length}
                </Badge>
                <Badge variant="outline" className="border-yellow-500 text-yellow-300">
                  Selected: {selectedDraftCount}
                </Badge>

                <div className="ml-auto flex items-center gap-2">
                  <Checkbox
                    id="showCancelled"
                    checked={showCancelled}
                    onCheckedChange={(v) => setShowCancelled(!!v)}
                  />
                  <Label htmlFor="showCancelled" className="text-sm text-gray-400 cursor-pointer">
                    Show cancelled shifts
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {viewMode === "day" && (
            <div className="space-y-3">
              <TimelineDayView
                shifts={filteredShiftsByDate[format(currentDate, "yyyy-MM-dd")] || []}
                dateStr={format(currentDate, "yyyy-MM-dd")}
                onEditShift={openEditDialog}
                onCreateShift={() => openCreateDialog(currentDate)}
              />
              {renderDayColumn(currentDate, true)}
            </div>
          )}

          {viewMode === "week" && (
            <WeekTimelineView
              shifts={filteredShifts}
              rangeDays={rangeDays}
              holidaysByDate={holidaysByDate}
              onEditShift={openEditDialog}
              onCreateShift={openCreateDialog}
              onPublishDay={handlePublishDay}
              onCopyAllocation={handleCopyAllocation}
              showCancelled={showCancelled}
              onFindCover={handleOpenFindCover}
              onShowTrends={handleOpenTrendOverlay}
              selectedShiftIds={selectedShiftIds}
              onToggleShiftSelected={toggleShiftSelected}
              showCoverage={showCoverage}
              violationsByShiftId={violationsByShiftId}
            />
          )}

          {viewMode === "month" && (
            <div>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {DAY_LABELS.map((label) => (
                  <div key={label} className="text-center text-xs font-medium text-gray-500 py-1">
                    {label}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {rangeDays.map((day) => {
                  const dateStr = format(day, "yyyy-MM-dd");
                  const dayShifts = filteredShiftsByDate[dateStr] || [];
                  const dayHolidays = holidaysByDate[dateStr] || [];
                  const today = isToday(day);
                  const inMonth = isSameMonth(day, currentDate);
                  const isDragOver = dragOverDate === dateStr;
                  const hasHoliday = dayHolidays.length > 0;

                  return (
                    <div
                      key={dateStr}
                      className={`rounded border min-h-[110px] flex flex-col transition-all ${
                        isDragOver
                          ? "border-blue-400 bg-blue-900/30 ring-1 ring-blue-400/50"
                          : hasHoliday
                          ? "border-orange-500/50 bg-orange-950/20"
                          : today
                          ? "border-blue-500 bg-blue-950/20"
                          : inMonth
                          ? "border-gray-700 bg-gray-800/40"
                          : "border-gray-800 bg-gray-900/30 opacity-50"
                      }`}
                      onDragOver={(e) => handleDragOver(e, dateStr)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, dateStr)}
                    >
                      {renderHolidayBanner(dateStr, true)}

                      <div className="p-1 flex items-center justify-between border-b border-gray-700/30">
                        <span
                          className={`text-xs font-bold ${
                            today ? "text-blue-300" : inMonth ? "text-white" : "text-gray-600"
                          }`}
                        >
                          {format(day, "d")}
                        </span>

                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 text-cyan-400 hover:text-cyan-300"
                            onClick={() => handleOpenTrendOverlay(dateStr)}
                            title="Show trends"
                          >
                            <span className="text-[9px] font-bold">T</span>
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 text-gray-500 hover:text-blue-400"
                            onClick={() => openCreateDialog(day)}
                          >
                            <Plus className="h-2.5 w-2.5" />
                          </Button>
                        </div>
                      </div>

                      <div className="p-0.5 flex-1 overflow-y-auto space-y-0.5 max-h-[120px]">
                        {dayShifts.slice(0, 4).map((shift) => (
                          <ShiftCard
                            key={shift.id}
                            shift={shift}
                            onEdit={openEditDialog}
                            compact
                            selectable={shift.status === "draft"}
                            selected={selectedShiftIds.includes(shift.id)}
                            onSelectedChange={(checked) =>
                              toggleShiftSelected(shift.id, checked)
                            }
                          />
                        ))}
                        {dayShifts.length > 4 && (
                          <p className="text-[9px] text-gray-500 text-center">
                            +{dayShifts.length - 4} more
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="demand" className="mt-4">
          {!scopedDriverNames && (
            <RosterDemandTab
              selectedDate={currentDate}
              onOpenDayOverlay={(dateStr) => {
                setTrendOverlayDate(dateStr);
                setTrendOverlayOpen(true);
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <Card className="bg-gray-800/60 border-gray-700">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700 text-gray-400 text-xs">
                      <th className="p-2 text-left">Select</th>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Driver</th>
                      <th className="p-2 text-left">Vehicle</th>
                      <th className="p-2 text-left">Type</th>
                      <th className="p-2 text-left">Group</th>
                      <th className="p-2 text-left">Vehicle Type</th>
                      <th className="p-2 text-left">Time</th>
                      <th className="p-2 text-left">Hours</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredShifts
                      .filter((s) => showCancelled || s.status !== "cancelled")
                      .map((shift) => (
                        <tr
                          key={shift.id}
                          className={`border-b border-gray-700/50 hover:bg-gray-700/30 ${
                            shift.status === "cancelled" ? "opacity-40" : ""
                          }`}
                        >
                          <td className="p-2">
                            {shift.status === "draft" ? (
                              <Checkbox
                                checked={selectedShiftIds.includes(shift.id)}
                                onCheckedChange={(checked) =>
                                  toggleShiftSelected(shift.id, !!checked)
                                }
                              />
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </td>

                          <td className="p-2 text-white text-xs">
                            {format(new Date(`${shift.shift_date}T00:00:00`), "EEE d MMM")}
                          </td>

                          <td className="p-2 text-white text-xs">
                            <span className="flex items-center gap-1">
                              {driverDisplayName(shift)}
                              {shift.repeat_group_id && (
                                <Repeat className="h-3 w-3 text-purple-400" />
                              )}
                            </span>
                          </td>

                          <td className="p-2 text-xs">
                            {shift.vehicle_number ? (
                              <span className="text-white flex items-center gap-1">
                                {getVehicleTypeIcon(shift.vehicle_type)}
                                {shift.vehicle_number}
                              </span>
                            ) : (
                              <span className="text-yellow-400">—</span>
                            )}
                          </td>

                          <td className="p-2">
                            <Badge
                              variant="outline"
                              className="text-[10px]"
                              style={{
                                borderColor: getShiftColour(shift),
                                color: getShiftColour(shift),
                              }}
                            >
                              {shift.shift_type_label}
                            </Badge>
                          </td>

                          <td className="p-2 text-gray-300 text-xs">
                            {getShiftGroupValue(shift) || "—"}
                          </td>

                          <td className="p-2 text-gray-300 text-xs">
                            {getVehicleGroupLabel(shift)}
                          </td>

                          <td className="p-2 text-gray-300 text-xs">
                            {shift.start_hhmm}–{shift.end_hhmm}
                          </td>

                          <td className="p-2 text-gray-300 text-xs">{shift.worked_hours}h</td>

                          <td className="p-2">
                            <Badge variant={getStatusBadgeVariant(shift.status)} className="text-[10px]">
                              {shift.status}
                            </Badge>
                          </td>

                          <td className="p-2">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-gray-400 hover:text-blue-400"
                                onClick={() => openEditDialog(shift)}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>

                              {shift.status !== "cancelled" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                                  onClick={() => handleCancelShift(shift.id)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              )}

                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                                onClick={() => handleDeleteShift(shift.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}

                    {filteredShifts.filter((s) => showCancelled || s.status !== "cancelled").length === 0 && (
                      <tr>
                        <td colSpan={11} className="p-8 text-center text-gray-500">
                          No shifts for this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="allocation" className="mt-4">
          {!scopedDriverNames && (
          <>
          <div className="flex flex-wrap items-center gap-4 mb-4 p-3 rounded-md bg-gray-800/60 border border-gray-700">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Night-before message includes:</span>
            {(
              [
                ["includeSedanSuv", "Sedan/SUV Jobs"],
                ["includeTmv", "TMV Jobs"],
                ["includeSts", "STS Jobs"],
                ["includeBreaks", "Breaks"],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                <Checkbox
                  checked={allocMsgOptions[key]}
                  onCheckedChange={(checked) => setAllocMsgOptions((o) => ({ ...o, [key]: !!checked }))}
                />
                {label}
              </label>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {rangeDays
              .filter((day) => viewMode !== "month" || isSameMonth(day, currentDate))
              .map((day) => {
                const dateStr = format(day, "yyyy-MM-dd");
                const dayGroups = computedAllocationGroups.filter((g) => g.shift_date === dateStr);
                const dayHolidays = holidaysByDate[dateStr] || [];

                if (viewMode === "month" && dayGroups.length === 0) return null;

                return (
                  <Card key={dateStr} className="bg-gray-800/60 border-gray-700">
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-sm text-white flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          {format(day, "EEEE")} {formatDate(day)}
                          {dayHolidays.length > 0 && (
                            <Badge
                              variant="outline"
                              className="text-[10px] border-orange-500 text-orange-400"
                            >
                              {dayHolidays[0].holiday_name}
                            </Badge>
                          )}
                        </span>

                        <span className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-gray-400 hover:text-blue-400"
                            onClick={() => handleCopyAllocation(dateStr)}
                          >
                            <Clipboard className="h-3 w-3 mr-1" /> Copy
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-gray-400 hover:text-blue-400"
                            onClick={() => setAllocMsgDateStr(dateStr)}
                          >
                            <Send className="h-3 w-3 mr-1" /> Message
                          </Button>
                        </span>
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-3 pt-0">
                      {dayGroups.length === 0 ? (
                        <p className="text-xs text-gray-500">No shifts</p>
                      ) : (
                        <div className="space-y-2">
                          {dayGroups.map((group, i) => (
                            <div key={i}>
                              <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-1">
                                {group.schedule_group}
                              </p>
                              <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono bg-gray-900/50 rounded p-2">
                                {group.grouped_text}
                              </pre>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
              .filter(Boolean)}
          </div>

          <AllocationMessageDialog
            dateStr={allocMsgDateStr}
            onClose={() => setAllocMsgDateStr(null)}
            allocationGroupsForDate={computedAllocationBuckets
              .filter((g) => g.shift_date === allocMsgDateStr)
              .map((g) => ({ schedule_group: g.schedule_group, shifts: g.shifts }))}
            vehicleTypes={vehicleTypes}
            options={allocMsgOptions}
          />
          </>
          )}
        </TabsContent>

        <TabsContent value="swaps" className="mt-4">
          <SwapRequestsPanel scopedDriverNames={scopedDriverNames} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="timeoff" className="mt-4">
          <AdminTimeOffPanel scopedDriverNames={scopedDriverNames} canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      <ShiftFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        formData={formData}
        setFormData={setFormData}
        editingShift={editingShift}
        drivers={drivers}
        vehicles={vehicles}
        shiftTypes={shiftTypes}
        saving={saving}
        onSave={handleSaveShift}
        onDelete={handleDeleteShift}
        onCancel={handleCancelShift}
        templates={[]}
      />

      <Dialog open={repeatConfirmOpen} onOpenChange={setRepeatConfirmOpen}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-purple-400" />
              Confirm Recurring Shifts
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-purple-950/30 border border-purple-700/50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400">Driver:</span>
                  <p className="text-white font-medium">{formData.driver_name}</p>
                </div>
                <div>
                  <span className="text-gray-400">Shift Type:</span>
                  <p className="text-white font-medium">{formData.shift_type_code}</p>
                </div>
                <div>
                  <span className="text-gray-400">Time:</span>
                  <p className="text-white font-medium">
                    {formData.start_time} – {formData.end_time}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">Pattern:</span>
                  <p className="text-white font-medium capitalize">
                    {formData.repeat_mode === "cycle"
                      ? `${formData.repeat_cycle_days_on} on / ${formData.repeat_cycle_days_off} off`
                      : formData.repeat_mode}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-300 mb-2">
                This will create{" "}
                <span className="text-purple-400 font-bold text-lg">{repeatDates.length}</span> shifts:
              </p>
              <div className="max-h-48 overflow-y-auto bg-gray-800 rounded-lg p-2 space-y-1">
                {repeatDates.map((dateStr) => (
                  <div key={dateStr} className="text-xs text-gray-300 flex items-center gap-2 py-0.5">
                    <CalendarDays className="h-3 w-3 text-gray-500" />
                    {format(new Date(`${dateStr}T00:00:00`), "EEEE, d MMMM yyyy")}
                    {holidaysByDate[dateStr] && (
                      <Badge
                        variant="outline"
                        className="text-[9px] border-orange-500 text-orange-400 px-1 py-0"
                      >
                        {holidaysByDate[dateStr][0].holiday_name}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRepeatConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRepeatConfirm}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Creating...
                </>
              ) : (
                <>Create {repeatDates.length} Shifts</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={recurringChoiceOpen} onOpenChange={setRecurringChoiceOpen}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-purple-400" />
              Recurring Shift
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-gray-300">
              This shift is part of a recurring series
              {seriesShiftCount > 0 && (
                <span className="text-purple-400 font-medium"> ({seriesShiftCount} shifts in total)</span>
              )}
              . Would you like to {recurringChoiceAction === "edit" ? "edit" : "delete"} just this
              occurrence, this and the following shifts, or every shift in the series?
            </p>

            {recurringChoiceShift && (
              <div className="bg-gray-800/80 border border-gray-700 rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Driver</span>
                  <span className="text-white font-medium">
                    {recurringChoiceShift.driver_firstname && recurringChoiceShift.driver_lastname
                      ? `${recurringChoiceShift.driver_firstname} ${recurringChoiceShift.driver_lastname}`
                      : recurringChoiceShift.driver_name}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Date</span>
                  <span className="text-white">
                    {format(new Date(`${recurringChoiceShift.shift_date}T00:00:00`), "EEE d MMM yyyy")}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Time</span>
                  <span className="text-white">
                    {recurringChoiceShift.start_hhmm} – {recurringChoiceShift.end_hhmm}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Type</span>
                  <Badge
                    variant="outline"
                    className="text-[10px]"
                    style={{
                      borderColor: getShiftColour(recurringChoiceShift),
                      color: getShiftColour(recurringChoiceShift),
                    }}
                  >
                    {recurringChoiceShift.shift_type_label}
                  </Badge>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Button
                className="w-full justify-start h-auto py-3 px-4 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-left"
                variant="outline"
                onClick={() => {
                  setRecurringChoiceOpen(false);
                  if (recurringChoiceShift) {
                    if (recurringChoiceAction === "edit") {
                      proceedToEditDialog(recurringChoiceShift, "single");
                    } else {
                      handleDeleteSingleFromSeries(recurringChoiceShift.id);
                    }
                  }
                }}
              >
                Just this occurrence
              </Button>

              <Button
                className="w-full justify-start h-auto py-3 px-4 bg-purple-950/30 hover:bg-purple-900/40 border border-purple-700/50 text-left"
                variant="outline"
                onClick={() => {
                  setRecurringChoiceOpen(false);
                  if (recurringChoiceShift) {
                    if (recurringChoiceAction === "edit") {
                      proceedToEditDialog(recurringChoiceShift, "following");
                    } else {
                      handleDeleteFollowingInSeries(recurringChoiceShift);
                    }
                  }
                }}
              >
                This and following shifts
                {followingShiftCount > 0 && (
                  <span className="ml-2 text-xs text-purple-300">({followingShiftCount} from this date on)</span>
                )}
              </Button>

              <Button
                className="w-full justify-start h-auto py-3 px-4 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-left"
                variant="outline"
                onClick={() => {
                  setRecurringChoiceOpen(false);
                  if (recurringChoiceShift) {
                    if (recurringChoiceAction === "edit") {
                      proceedToEditDialog(recurringChoiceShift, "series");
                    } else if (recurringChoiceShift.repeat_group_id) {
                      handleDeleteEntireSeries(recurringChoiceShift.repeat_group_id);
                    }
                  }
                }}
              >
                All shifts in series, including past ones
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecurringChoiceOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FindCoverDialog
        open={findCoverOpen}
        onOpenChange={(v) => {
          setFindCoverOpen(v);
          if (!v) setFindCoverShift(null);
        }}
        absentShift={findCoverShift}
        drivers={drivers}
        vehicles={vehicles}
        shifts={shifts}
        shiftTypes={shiftTypes}
        unavailableVehicleKeys={unavailableKeys}
        onCoverCreated={() => {
          fetchAll();
        }}
      />

      <PublishValidationDialog
        open={publishValidationOpen}
        onOpenChange={setPublishValidationOpen}
        conflicts={publishConflicts}
        scopeLabel={
          publishScope.kind === "day" && publishScope.dateStr
            ? `drafts for ${format(new Date(`${publishScope.dateStr}T00:00:00`), "EEE d MMM")}`
            : publishScope.kind === "selected"
            ? "selected drafts"
            : "drafts in this range"
        }
        onReassign={(shift) => {
          setPublishValidationOpen(false);
          openEditDialog(shift);
        }}
        onConfirmPublishAnyway={() => doPublish(publishScope)}
        confirming={publishing}
      />

      <DayTrendOverlay
        open={trendOverlayOpen}
        onOpenChange={setTrendOverlayOpen}
        selectedDateStr={trendOverlayDate}
      />
    </div>
  );
};

export default RosterPage;